import React, { useState, useEffect } from "react";
import { Card, Alert } from "react-bootstrap";
import { IActionHandler, OrgInfo, AdminUserBase, Sort } from "@preveil-api";
import {
  Account, AdminMessages, AdminErrorMessages, AdminSuccessMessages, AdminWarningMessages, collectionApi, Message, MessageAnchors, MessageHandlerDisplayType, GlobalErrorMessages,
  MessageToastTypes, useAppDispatch, useGetOrgInfo, useGetUsersOrgsByEntityIdGatewayQuery, usePostUsersOrgsByEntityIdGatewayAliasesRemoveMutation, usePutUsersOrgsByEntityIdGatewayEnabledMutation, usePutUsersOrgsByEntityIdGatewayUserMutation, GatewayConnectivityStatus, useAppSelector, SORT_DIRECTION,
  getOppositeSortDirection, sortStrings, EmailGatewayActions, UserResultsMode
} from "src/common";
import { DefaultTemplate, PageHeader, AlertWithSwitch, InfoPopover } from "src/components";
import { Toolbar, NoAddressesView, ListView, AddToEmailGateway, TopPanel } from ".";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
}

// Description: Email Gateway (now known as Email Relay) component.
function EmailGatewayComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [show_modal, setShowModal] = useState<boolean>(false);
  const [email_gateway_list, setEmailGatewayList] = useState<string[]>([]);
  const [filtered_list, setFilteredList] = useState<string[]>([]);
  const [gateway_user_id, setGatewayUserId] = useState<string>("");
  const [is_email_gateway_enabled, setEmailGatewayStatus] = useState<boolean>(false);
  const [organization_info, setOrganizationInfo] = useState<AdminUserBase[]>([]);
  const [filter_param, setFilterParam] = useState<string>("");
  const [gateway_connectivity_status, setGatewayConnectivityStatus] = useState<GatewayConnectivityStatus>();
  const [is_invalid_gateway_id, setIsInvalidGatewayId] = useState<boolean>(false);
  const [is_loading, setIsLoading] = useState(true);
  const [is_user_loading, setIsUserIdLoading] = useState<boolean>(true);
  const [sort, setSort] = useState<Sort<string>>({ field: "address", direction: "asc" });
  const [postUsersOrgsByEntityIdGatewayAliasesRemove] = usePostUsersOrgsByEntityIdGatewayAliasesRemoveMutation();
  const [putUsersOrgsByEntityIdGatewayUser] = usePutUsersOrgsByEntityIdGatewayUserMutation();
  const [putUsersOrgsByEntityIdGatewayEnabled] = usePutUsersOrgsByEntityIdGatewayEnabledMutation();
  const [getUsersOrgsByEntityIdGatewayStatus] = collectionApi.endpoints.getUsersOrgsByEntityIdGatewayStatus.useLazyQuery();
  const [getUsersOrgsByEntityIdGateway] = collectionApi.endpoints.getUsersOrgsByEntityIdGateway.useLazyQuery();
  const { data, error, refetch, isFetching } = useGetUsersOrgsByEntityIdGatewayQuery({
    account_ids: Account.getAccountIdentifiers(current_account),
    body: {
      entityId: org_info.org_id
    }
  });

  const { data: org_data, error: org_error, refresh } = useGetOrgInfo(current_account, org_info, UserResultsMode.lightweight);

  // Description: Fetch org info on mount.
  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!!current_account) {
      getGatewayConnectivityStatus();
    }
  }, [current_account]);

  useEffect(() => {
    if (!isFetching) {
      refetch();
    }
  }, []);

  useEffect(() => {
    const is_data_fetched = !_.isEmpty(data) && !!org_data;
    if (is_data_fetched) {
      try {
        const { enabled, user_id } = data;
        user_id !== gateway_user_id && setGatewayUserId(user_id);
        enabled !== is_email_gateway_enabled && setEmailGatewayStatus(enabled);
        setOrganizationInfo(org_data.lightweight_org_users);
        setIsUserIdLoading(false);
      } catch (error) {
        const params = {
          message: AdminErrorMessages.error_fetching_gateway_users_data,
          stack: error
        };
        dispatchPageError(params);
      }
    }

    if (error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_fetching_email_gateway), error));
      setIsUserIdLoading(false);
    }
    if (org_error) {
      const params = {
        message: AdminErrorMessages.error_fetching_org_info,
        stack: org_error
      };
      dispatchPageError(params);
      setIsUserIdLoading(false);
    }
  }, [data, error, org_data, org_error]);

  useEffect(() => {
    if (data && data?.aliases) {
      const aliases = sortStrings(data.aliases, { field: sort.field, direction: sort.direction });
      setEmailGatewayList(aliases);
      setFilteredList(aliases);
      setIsLoading(false);
    }
  }, [data?.aliases]);

  // Description: dispatch any error found in the page.
  function dispatchPageError(params: { message: string, stack?: any }) {
    dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.toastr), params.stack));
  }

  // Description: Connectivity Test status.
  async function getGatewayConnectivityStatus(notification?: { show: boolean }) {
    const account_ids = Account.getAccountIdentifiers(current_account);
    const body = { entityId: org_info.org_id };
    await getUsersOrgsByEntityIdGatewayStatus({
      account_ids, body
    })
      .unwrap()
      .then((response) => {
        setGatewayConnectivityStatus(response);
        // We don't want to notify the user on page load, just fetch what the status is.
        if (notification?.show) {
          dispatch(
            uiActions.handleSetMessage(
              new Message(AdminSuccessMessages.success_gateway_connectivity_test),
            ),
          );
        }
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_gateway_connectivity_test,
          stack: error
        };
        dispatchPageError(params);
      });

  }

  // Description: Use for Refresh action in Toolbar. Fetches list of aliases.
  async function refreshGatewayList() {
    setIsLoading(true);
    const account_ids = Account.getAccountIdentifiers(current_account);
    const body = { entityId: org_info.org_id };
    await getUsersOrgsByEntityIdGateway({
      account_ids, body
    })
      .unwrap()
      .then((result) => {
        const { aliases } = result;
        setEmailGatewayList(aliases);
        EmailGatewayRequests.handleSearchParam(filter_param);
        setIsLoading(false);
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_fetching_gateway_aliases,
          stack: error
        };
        dispatchPageError(params);
      });

  }

  // Description: Set/Change/Remove Gateway User ID.
  // To remove the gateway user_id, the email gateway must be disabled first.
  async function changeGatewayUserIdRequest(user_id: string | null, action: keyof typeof EmailGatewayActions) {
    if (action === EmailGatewayActions.remove && is_email_gateway_enabled) {
      // Handling the odd case where the user is trying to remove the gateway user_id while the gateway is still enabled.
      const params = {
        message: AdminWarningMessages.warning_removing_gateway_user_id
      };
      dispatchPageError(params);
      return;
    }
    const account_ids = Account.getAccountIdentifiers(current_account);
    await putUsersOrgsByEntityIdGatewayUser({
      account_ids,
      body: {
        entityId: org_info.org_id,
        user_id
      }
    })
      .unwrap()
      .then(() => {
        setGatewayUserId(user_id || "");
        setIsInvalidGatewayId(false);
        const action_message = action === EmailGatewayActions.set ? "changed" : "removed";
        const message = AdminSuccessMessages.success_email_gateway_user_id_change.replace(MessageAnchors.message_content, action_message);
        dispatch(
          uiActions.handleSetMessage(
            new Message(message),
          ),
        );
      })
      .catch((error) => {
        let params;
        const { status, statusText, data } = error;
        // If user is not part of the organization it will return a 409 status error.
        // Note Update: now we are filtering out non-org users, so it shouldn't return this error
        // But just keeping it in case.
        if (status === 409 && statusText === "Conflict") {
          const { errors } = data;
          const { cause: user_id } = errors[0];
          params = {
            message: AdminErrorMessages.error_gateway_user_no_org.replace(MessageAnchors.user_id, user_id),
            stack: errors
          };
        } else {
          params = {
            message: AdminErrorMessages.error_gateway_assign_user,
            stack: error
          };
        }
        setIsInvalidGatewayId(true);
        dispatchPageError(params);
      });
  };

  // Description: Remove Aliases (addresses) from Email Gateway
  async function removeFromEmailGatewayAddresses(addresses: string[]) {
    const aliases = _.isArray(addresses) ? addresses : selected;
    const account_ids = Account.getAccountIdentifiers(current_account);
    await postUsersOrgsByEntityIdGatewayAliasesRemove({
      account_ids,
      body: {
        entityId: org_info.org_id,
        aliases
      }
    })
      .unwrap()
      .then(() => {
        if (selected.length > 1 && addresses) {
          const new_selected = selected.filter((address) => !addresses.includes(address));
          dispatch(adminActions.setSelected(new_selected));
        } else {
          dispatch(adminActions.setSelected([]));
        }
        dispatch(
          uiActions.handleSetMessage(
            new Message(AdminSuccessMessages.success_delete_email_gateway_addresses),
          ),
        );
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_removing_email_gateway,
          stack: error
        };
        dispatchPageError(params);
      });
  };

  // Description: Enable/Disable Email Gateway org.
  async function toggleEmailGatewayRequest() {
    const account_ids = Account.getAccountIdentifiers(current_account);
    await putUsersOrgsByEntityIdGatewayEnabled({
      account_ids,
      body: {
        entityId: org_info.org_id,
        enabled: !is_email_gateway_enabled
      }
    })
      .unwrap()
      .then(() => {
        const message = is_email_gateway_enabled ? AdminSuccessMessages.success_disable_email_gateway : AdminSuccessMessages.success_enable_email_gateway;
        setEmailGatewayStatus(!is_email_gateway_enabled);
        dispatch(uiActions.handleSetMessage(new Message(message)));
      })
      .catch((error) => {
        const gateway_status = is_email_gateway_enabled ? "Disabling" : "Enabling";
        const message = AdminErrorMessages.error_toggle_email_gateway.replace(MessageAnchors.message_content, gateway_status);
        const params = {
          message,
          stack: error
        };
        dispatchPageError(params);
      });
  }

  // Description: Handles the confirmation dialog, warning user if they want to
  // delete the selected addresses/domains.
  function confirmDeleteAddressesDialog(addresses: string[]) {
    const confirmation_dialog = new Message(
      AdminMessages.confirm_delete_email_gateway.body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.confirm_delete_email_gateway.title,
      {
        label: "Yes",
        data: true,
        action: () => removeFromEmailGatewayAddresses(addresses)
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: handles state email gateway (active/no active).
  function handleToggleEmailGateway() {
    const dialog_message = is_email_gateway_enabled ? AdminMessages.disable_gateway : AdminMessages.enable_gateway;
    const confirmation_dialog = new Message(
      dialog_message.body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      dialog_message.title,
      {
        label: "Yes",
        data: true,
        action: () => toggleEmailGatewayRequest()
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  const EmailGatewayRequests = {
    handleSetSort: (params: "address") => {
      const direction = sort.field === params ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending;
      const sorted_requests = sortStrings(filtered_list, { field: params, direction });
      setSort({ field: params, direction });
      setFilteredList(sorted_requests);
    },
    handleGetRefreshRequest: () => {
      refreshGatewayList();
    },
    handleSearchParam: (param?: string) => {
      if (!!param) {
        setFilterParam(param.toLowerCase());
        const filtered = email_gateway_list.filter((address: string) => {
          return address.toLowerCase().includes(param.toLowerCase());
        });
        setFilteredList(filtered);
      } else {
        setFilterParam("");
        setFilteredList(email_gateway_list);
      }
    },
    handleSelect: (params: { address?: string, selected: boolean }) => {
      let new_selected = selected.slice();
      if (!!params.address) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === params.address)) &&
            new_selected.push(params.address);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === params.address));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        new_selected = params.selected ? filtered_list : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    handleDelete: (addresses: string[]) => {
      confirmDeleteAddressesDialog(addresses);
    },
    handleShowModal: () => {
      setShowModal(!show_modal);
    },
    handleTestGatewayConnectivity: () => {
      getGatewayConnectivityStatus({ show: true });
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      dispatchPageError(params);
    },
    handleChangeGatewayUserId: (user_id: string) => {
      changeGatewayUserIdRequest(user_id, EmailGatewayActions.set);
    },
    handleRemoveGatewayUserId: () => { 
      changeGatewayUserIdRequest(null, EmailGatewayActions.remove);
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((EmailGatewayRequests as any)[callback] instanceof Function) {
      (EmailGatewayRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      EmailGatewayRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  return <DefaultTemplate className="admin-component content-wrapper">
    <PageHeader>
      <h1>Secure Email Relay</h1>
    </PageHeader>
    <Card className="card-section">
      <Alert className="gateway-steps" variant="light" dismissible={true}>
        <p>Steps to activate Secure Email Relay</p>
        <ol>
          <li>Assign a relay user ID.</li>
          <li>Toggle the secure email relay to on.</li>
          <li>Define the controlled address and domain list.</li>
        </ol>
      </Alert>
      <Card className="gateway-card">
        {!is_user_loading ? (
          <TopPanel
            org_users={organization_info}
            is_invalid_gateway_id={is_invalid_gateway_id}
            is_email_gateway_enabled={is_email_gateway_enabled}
            gateway_user_id={!!gateway_user_id ? [gateway_user_id] : []}
            gateway_connectivity_status={gateway_connectivity_status}
            current_account={current_account}
            handleAction={handlePageActions} />
        ) : null}
      </Card>
      <AlertWithSwitch
        popover={false}
        disabled={is_loading || !gateway_user_id}
        toggleState={is_email_gateway_enabled}
        handleChange={handleToggleEmailGateway}
      >
        <label className="align-center">
          <span>{`The organization's Secure Email Relay is ${is_email_gateway_enabled ? "enabled" : "disabled"
            }.`}</span>
          <InfoPopover
            message={is_email_gateway_enabled ? AdminMessages.enabled_gateway_info : AdminMessages.disabled_gateway_info}
            placement="right"
          />
        </label>
      </AlertWithSwitch>

      {!!gateway_user_id && !!is_email_gateway_enabled &&
        <>
          <AddToEmailGateway
            org_info={org_info}
            current_account={current_account}
            show_modal={show_modal}
            handleAction={handlePageActions}
            address_list={filtered_list}
          />
          <Toolbar is_loading={is_loading} address_list_length={email_gateway_list.length} filtered_list_length={filtered_list.length} handleAction={handlePageActions} filter_param={filter_param} />
          {email_gateway_list.length === 0 && !is_loading ?
            <NoAddressesView handleAction={handlePageActions} /> :
            <div id="outer-scope" className="cover-content">
              <ListView address_list={filtered_list} is_loading={is_loading} selected={selected} sort={sort} handleAction={handlePageActions} />
            </div>
          }
        </>
      }
    </Card>
  </DefaultTemplate>;
}

export default React.memo(EmailGatewayComponent);
